import cx from 'classnames'
import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  HTMLInputAutoCompleteAttribute,
  ReactNode,
  useId,
} from 'react'

export interface InputFieldWithManagedStateProps {
  type: 'text' | 'email' | 'number' | 'password'
  name?: string
  value?: string
  setValue: (newValue: string) => void
  label?: string | ReactNode
  isRequired?: boolean
  placeholder?: string
  min?: string | number
  max?: string | number
  maxLength?: number
  autoComplete?: HTMLInputAutoCompleteAttribute
  errorMessage?: string
  borderBottom?: boolean
  isInvertedColors?: boolean
  hasCustomInputStyles?: boolean
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  className?: string
  labelClassName?: string
  inputWrapperClassName?: string
  inputClassName?: string
  children?: ReactNode
}

const InputFieldWithManagedState = forwardRef<
  HTMLInputElement,
  InputFieldWithManagedStateProps
>(
  (
    {
      type,
      name,
      value,
      setValue,
      label,
      isRequired,
      placeholder,
      min,
      max,
      maxLength,
      autoComplete,
      errorMessage,
      borderBottom,
      isInvertedColors,
      hasCustomInputStyles,
      onChange,
      onBlur,
      className,
      labelClassName,
      inputWrapperClassName,
      inputClassName,
      children,
    },
    ref,
  ) => {
    const id = useId()

    const inputMode =
      type === 'email' ? 'email' : type === 'number' ? 'decimal' : 'text'

    return (
      <div className={cx('grid', className)}>
        <div className="flex flex-col relative text-left">
          {!!label && (
            <label
              htmlFor={id}
              className={cx(
                'text-sm',
                {
                  'mb-1': !hasCustomInputStyles,
                },
                labelClassName,
              )}
            >
              {label}
              {!!isRequired && <span className="ml-0.5 text-red">*</span>}
            </label>
          )}

          <div
            className={cx(
              'flex justify-between',
              {
                'border-error': !hasCustomInputStyles && errorMessage,
                'border-input-border':
                  !hasCustomInputStyles && !errorMessage && !isInvertedColors,
                'border-input-inverted-border':
                  !hasCustomInputStyles && !errorMessage && isInvertedColors,
                'border-b': !hasCustomInputStyles && borderBottom,
                'border px-4': !hasCustomInputStyles && !borderBottom,
              },
              inputWrapperClassName,
            )}
          >
            <input
              ref={ref}
              id={id}
              type={type}
              inputMode={inputMode}
              min={min}
              max={max}
              maxLength={maxLength}
              autoComplete={autoComplete}
              name={name}
              value={value}
              placeholder={placeholder}
              onChange={(event) => {
                if (value !== event.target.value) {
                  setValue(event.target.value)
                }

                if (onChange) {
                  onChange(event)
                }
              }}
              onBlur={(event) => {
                if (value !== event.target.value) {
                  setValue(event.target.value)
                }

                if (onBlur) {
                  onBlur(event)
                }
              }}
              className={cx(
                'relative appearance-none w-full h-full',
                {
                  'py-2 text-base leading-none': !hasCustomInputStyles,
                  'bg-input-bg text-input-text placeholder-input-placeholder':
                    !hasCustomInputStyles && !isInvertedColors,
                  'bg-input-inverted-bg text-input-inverted-text placeholder-input-inverted-placeholder':
                    !hasCustomInputStyles && isInvertedColors,
                },
                inputClassName,
              )}
            />
            {children}
          </div>

          {errorMessage && (
            <span role="alert" className="mt-2 text-xs text-error">
              {errorMessage}
            </span>
          )}
        </div>
      </div>
    )
  },
)

InputFieldWithManagedState.displayName = 'InputFieldWithManagedState'

export default InputFieldWithManagedState
